import Link from 'next/link';
import type { AuthConfig } from '@sso/auth-client';
import { buildLogoutUrl } from '@sso/auth-client';

import { useGtmAuthEvents } from '@workspace/gtm/nextjs';
import { CallIcon } from '@workspace/lib/components/atoms/icons/CallIcon';
import { ContactPhoneIcon } from '@workspace/lib/components/atoms/icons/ContactPhoneIcon';
import { IconAsButton } from '@workspace/lib/components/molecules/buttons/IconAsButton';
import { CIRCLE_ICON_BUTTON_CLASS_NAME } from '@workspace/lib/constants/icon-button';
import { useDeviceInfo } from '@workspace/lib/hooks/common/use-device-info';
import { useSessionStore } from '@workspace/lib/hooks/store/use-session-store';
import { useStore } from '@workspace/lib/hooks/store/use-store';
import { cn } from '@workspace/lib/utils/cn';

export interface NavbarProps {
  Logo: React.ReactNode;
  primaryText?: string | null;
  secondaryText?: string | null;
  isOfferOrThankYouPage?: boolean;
  logoutButtonText?: string | null;
  phoneNumber?: string | null;
  compactPhoneNumber?: string | null;
  isCompactPhoneDisabled?: boolean;
  authConfig?: AuthConfig | null;
  /**
   * Absolute URL the SSO portal sends the user to after logout. Defaults to the
   * app origin. Its host must be in the SSO portal's ALLOWED_REDIRECT_DOMAINS,
   * otherwise the portal falls back to its own /login page.
   */
  logoutReturnUrl?: string | null;
  profile?: React.ReactNode;
}

export function Navbar({
  Logo,
  primaryText,
  secondaryText,
  isOfferOrThankYouPage,
  logoutButtonText,
  phoneNumber,
  compactPhoneNumber,
  isCompactPhoneDisabled = false,
  authConfig,
  logoutReturnUrl,
  profile,
}: NavbarProps) {
  const { isMobileDevice } = useDeviceInfo();
  const { resetSessionStore } = useSessionStore();
  const { resetStore } = useStore();
  const { fireLogout } = useGtmAuthEvents();

  const handleLogout = () => {
    if (!authConfig) {
      console.error('AuthConfig is not provided for logout functionality.');
      return;
    }

    resetSessionStore();
    resetStore();
    fireLogout();
    window.location.href = buildLogoutUrl(
      authConfig,
      logoutReturnUrl || window.location.origin,
    );
    return;
  };

  return (
    <nav
      className={cn(
        'relative z-20 w-full bg-white sm:border-b sm:border-neutral-500',
        {
          'border-b border-neutral-500': isOfferOrThankYouPage,
        },
      )}
    >
      <div className='mx-auto px-4 py-2 sm:px-6 md:px-8 md:py-4 lg:px-12'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-6'>{Logo}</div>

          <div className='flex items-center gap-4 md:gap-8'>
            {!isMobileDevice && profile && (
              <div className='hidden lg:block'>{profile}</div>
            )}
            {!isMobileDevice && profile && logoutButtonText && (
              <div className='hidden h-8 w-px bg-neutral-300 lg:block' />
            )}
            {phoneNumber && (
              <Link
                href={`tel:${phoneNumber.replace(/\D/g, '')}`}
                className='flex items-center gap-2'
              >
                <span className='text-xs font-bold text-blue-600 md:text-sm'>
                  {phoneNumber}
                </span>
                <ContactPhoneIcon />
              </Link>
            )}
            {compactPhoneNumber && (
              <Link
                href={`tel:${compactPhoneNumber.replace(/\D/g, '')}`}
                aria-label={compactPhoneNumber}
                aria-disabled={isCompactPhoneDisabled || undefined}
                tabIndex={isCompactPhoneDisabled ? -1 : undefined}
                className={cn(
                  CIRCLE_ICON_BUTTON_CLASS_NAME,
                  'cursor-pointer transition duration-150 ease-out hover:scale-[1.02] lg:hidden',
                )}
              >
                <CallIcon />
              </Link>
            )}

            {logoutButtonText &&
              (isMobileDevice ? (
                <IconAsButton
                  iconName='logout'
                  type='button'
                  disabled={false}
                  textContent=''
                  onClick={handleLogout}
                  className={CIRCLE_ICON_BUTTON_CLASS_NAME}
                />
              ) : (
                <>
                  <button
                    onClick={handleLogout}
                    className='border-blue-primary text-blue-primary hidden cursor-pointer items-center gap-2 rounded-full border px-4 py-2 transition-colors hover:bg-blue-50 lg:flex'
                  >
                    {logoutButtonText}
                  </button>
                  <IconAsButton
                    iconName='logout'
                    type='button'
                    disabled={false}
                    textContent=''
                    onClick={handleLogout}
                    className={cn(
                      CIRCLE_ICON_BUTTON_CLASS_NAME,
                      'self-center lg:hidden',
                    )}
                  />
                </>
              ))}

            {primaryText && (
              <div className='flex flex-col text-end md:flex-row md:items-center md:gap-4'>
                <p className='text-black-primary text-sm font-bold'>
                  {primaryText}
                </p>

                {secondaryText && (
                  <p className='font-branding text-black-primary mt-[-3px] text-xs font-semibold md:text-base'>
                    {secondaryText}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {isMobileDevice && profile && (
        <div className='mt-2 border-t border-neutral-200'>
          <div className='px-4 pt-2'>{profile}</div>
        </div>
      )}
    </nav>
  );
}
